/**
 * Display formatters for the Engagement Hub prototype.
 *
 * These choices are presentational — the spec does not specify exact display
 * formats. If/when the design system grows a currency formatter or a
 * relative-time formatter, swap these for the canonical helpers.
 */
import type { BillingAccount, EngagementFundingMethod } from '../../types';

const usdCompactFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const usdPreciseFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatUsd(cents: number, opts: { showCents?: boolean } = {}): string {
  const { showCents = false } = opts;
  return showCents ? usdPreciseFmt.format(cents / 100) : usdCompactFmt.format(cents / 100);
}

/**
 * Formats `customer_rate_loaded_cents` for customer-side display. Display
 * denomination derives from the engagement's `funding_method` per spec §6.3:
 *   - `'credits'`     → render as customer-local credits, derived via
 *                       `cents / billing_account.credit_to_usd_rate_cents`.
 *   - `'usd_invoice'` → render in USD.
 */
export function formatCustomerRate(
  cents: number,
  fundingMethod: EngagementFundingMethod,
  ba: BillingAccount
): string {
  if (fundingMethod === 'credits') {
    const credits = cents / ba.credit_to_usd_rate_cents;
    const display = Number.isInteger(credits) ? credits.toString() : credits.toFixed(2);
    return `${display} credits`;
  }
  return formatUsd(cents, { showCents: true });
}

/**
 * Customer-local credits at one decimal place — "27.9", "20.0". Used by the
 * §11.2 #27 credits-path banner copy, where NTE and adjustment amounts on
 * credit-funded engagements render in credits rather than dollars. (Distinct
 * from `formatCustomerRate`, which renders per-hour rates at two decimals.)
 */
export function formatCreditsAmount(cents: number, ba: BillingAccount): string {
  return (cents / ba.credit_to_usd_rate_cents).toFixed(1);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The date-only vs instant contract, decided by the value's shape because that
 * is what the api's scalar decides: a calendar date (GraphQL `Date`, e.g. an
 * hours period) arrives as `YYYY-MM-DD` and renders as that local calendar
 * day in every timezone; an instant (`Datetime`/`DateTime`, e.g. `createdAt`)
 * arrives as a full ISO timestamp and converts to the viewer's timezone.
 *
 * A date-only field exposed as an instant renders a day early west of UTC,
 * which is why it must cross the wire as `Date` (ENG-5747). Never strip the
 * time off an instant to "fix" that here: billing period bounds and audit
 * timestamps are real instants.
 */
function parseFlexible(s: string): Date {
  if (DATE_ONLY.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(s);
}

/**
 * Format an hourly rate. Always shows cents (e.g. "$312.50") because rates
 * routinely include them. For round numbers, ".00" is intentional — keeps
 * the rate column visually aligned with non-round rates in the same view.
 */
export function formatRate(cents: number): string {
  return formatUsd(cents, { showCents: true });
}

/**
 * Human-readable file size from bytes, for §9.8 message-attachment chips.
 * Whole numbers for B and KB, one decimal for MB and up. Presentational —
 * swap for a design-system helper if one lands. (1 KB = 1024 B.)
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** "Apr 1, 2026" */
export function formatDate(iso: string): string {
  return parseFlexible(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "Apr 1" */
export function formatDateShort(iso: string): string {
  return parseFlexible(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/** "Apr 21–25" — uses en-dash; year shown if periods cross a year boundary. */
export function formatPeriod(startIso: string, endIso: string): string {
  const start = parseFlexible(startIso);
  const end = parseFlexible(endIso);
  if (start.getFullYear() !== end.getFullYear()) {
    return `${formatDate(startIso)} – ${formatDate(endIso)}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${formatDateShort(startIso)} – ${formatDateShort(endIso)}, ${start.getFullYear()}`;
  }
  return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
}

/**
 * Given a finished period's `period_end`, derive the next contiguous period
 * boundaries. Mirrors the cadence pattern used by the Joe Arnold mock:
 * 5-day periods, next one starting 3 days after the previous `period_end`
 * (e.g., end Sat May 2 → start Tue May 5, end Sat May 9).
 *
 * TODO: confirm with eng — actual period derivation rule. The spec
 * (§6.4) defines period as a free `period_start`/`period_end` pair and
 * does not pin it to a calendar week or to NTE pacing config. This helper
 * encodes the prototype mock's pattern, not a contract.
 */
export function nextWeeklyPeriod(latestEndIso: string): {
  period_start: string;
  period_end: string;
} {
  const end = parseFlexible(latestEndIso);
  const start = new Date(end);
  start.setDate(end.getDate() + 3);
  const nextEnd = new Date(start);
  nextEnd.setDate(start.getDate() + 4);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { period_start: fmt(start), period_end: fmt(nextEnd) };
}

/**
 * True if `now` falls within the inclusive period bounds. Used to gate the
 * synthetic "current period not yet submitted" row in the Hours table.
 */
export function isCurrentPeriod(startIso: string, endIso: string, now: Date): boolean {
  const start = parseFlexible(startIso);
  const end = parseFlexible(endIso);
  // Treat period boundaries inclusively at day precision.
  const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const dayEndExclusive = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
  return now >= dayStart && now < dayEndExclusive;
}

function ago(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'} ago`;
}

/**
 * "2 days ago", "Apr 1" for anything five weeks or older.
 *
 * Units are spelled out rather than abbreviated (`2d`, `4w`). QA read the
 * abbreviated form as over-terse on the Hub's Activity list (ENG-5448), and
 * these render in full-width column contexts where the words fit. Spelled out
 * across the whole ladder, not just weeks — the mixed form ("4 weeks ago"
 * beside "6d ago") is the same within-one-surface inconsistency that report
 * was about.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const diffMs = now.getTime() - then.getTime();
  if (diffMs < 0) return formatDate(iso);
  const diffMins = Math.round(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return ago(diffMins, 'minute');
  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return ago(diffHours, 'hour');
  const diffDays = Math.round(diffMs / 86400000);
  if (diffDays < 7) return ago(diffDays, 'day');
  const diffWeeks = Math.round(diffDays / 7);
  if (diffWeeks < 5) return ago(diffWeeks, 'week');
  return formatDate(iso);
}

/**
 * Derive the next billing date from start_date + billing_cadence.
 *
 * Simple model: monthly cadence bills the 1st of the month after `asOf`;
 * biweekly bills 14 days from the most recent biweekly anchor on `start`.
 *
 * TODO: confirm with eng — actual cadence/anchoring logic.
 */
export function nextBillingDate(
  startIso: string,
  cadence: 'monthly' | 'biweekly',
  asOf: Date
): string {
  if (cadence === 'biweekly') {
    const start = new Date(startIso);
    const elapsedDays = Math.floor((asOf.getTime() - start.getTime()) / 86400000);
    const periodsCompleted = Math.floor(elapsedDays / 14);
    const next = new Date(start);
    next.setDate(start.getDate() + (periodsCompleted + 1) * 14);
    return next.toISOString();
  }
  // monthly — first of next month
  const next = new Date(asOf.getFullYear(), asOf.getMonth() + 1, 1);
  return next.toISOString();
}
